import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Wallet,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Banknote,
  Briefcase,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/lib/format";
import CreditCardHealth from "@/components/CreditCardHealth";

export const ACCOUNT_TYPES = [
  { value: "cash", label: "Efectivo" },
  { value: "debit", label: "Débito" },
  { value: "credit", label: "Crédito" },
  { value: "savings", label: "Ahorros" },
  { value: "investment", label: "Inversión" },
  { value: "income", label: "Ingresos" },
];

export const ACCOUNT_ICONS = [
  { value: "wallet", label: "Cartera", Icon: Wallet },
  { value: "credit-card", label: "Tarjeta", Icon: CreditCard },
  { value: "piggy-bank", label: "Alcancía", Icon: PiggyBank },
  { value: "trending-up", label: "Inversión", Icon: TrendingUp },
  { value: "banknote", label: "Billete", Icon: Banknote },
  { value: "briefcase", label: "Trabajo", Icon: Briefcase },
];

export const ACCOUNT_COLORS = [
  "#00C97D", "#0052FF", "#F97316", "#EF4444",
  "#8B5CF6", "#EAB308", "#06B6D4", "#64748B",
];

export function getAccountIconComp(name) {
  const found = ACCOUNT_ICONS.find((i) => i.value === name);
  return found ? found.Icon : Wallet;
}

export function getAccountTypeLabel(t) {
  return ACCOUNT_TYPES.find((x) => x.value === t)?.label || t;
}

const emptyForm = {
  name: "",
  type: "cash",
  color: ACCOUNT_COLORS[0],
  icon: "wallet",
  initial_balance: "0",
  note: "",
  credit_limit: "",
  cutoff_day: "",
  payment_due_day: "",
};

export default function Accounts() {
  const { user } = useAuth();
  const currency = user?.currency || "MXN";
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounts");
      setAccounts(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Error al cargar cuentas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (acc) => {
    setEditing(acc);
    setForm({
      name: acc.name,
      type: acc.type,
      color: acc.color || ACCOUNT_COLORS[0],
      icon: acc.icon || "wallet",
      initial_balance: String(acc.initial_balance ?? 0),
      note: acc.note || "",
      credit_limit: acc.credit_limit != null ? String(acc.credit_limit) : "",
      cutoff_day: acc.cutoff_day != null ? String(acc.cutoff_day) : "",
      payment_due_day: acc.payment_due_day != null ? String(acc.payment_due_day) : "",
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Pon un nombre a la cuenta");
      return;
    }
    const payload = {
      name: form.name.trim(),
      type: form.type,
      color: form.color,
      icon: form.icon,
      initial_balance: parseFloat(form.initial_balance || "0") || 0,
      note: form.note.trim(),
    };
    if (form.type === "credit") {
      const limit = parseFloat(form.credit_limit);
      const cutoff = parseInt(form.cutoff_day, 10);
      const payDue = parseInt(form.payment_due_day, 10);
      if (!isNaN(limit) && limit >= 0) payload.credit_limit = limit;
      if (!isNaN(cutoff) && cutoff >= 1 && cutoff <= 31) payload.cutoff_day = cutoff;
      if (!isNaN(payDue) && payDue >= 1 && payDue <= 31) payload.payment_due_day = payDue;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/accounts/${editing.id}`, payload);
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/accounts", payload);
        toast.success("Cuenta creada");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (acc) => {
    try {
      await api.post(`/accounts/${acc.id}/make-default`);
      toast.success(`${acc.name} ahora es predeterminada`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Error");
    }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`¿Eliminar "${acc.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/accounts/${acc.id}`);
      toast.success("Cuenta eliminada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Error al eliminar");
    }
  };

  return (
    <Layout>
      <section className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="accounts-hero">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] mb-2 font-medium">
            Tus cuentas
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#F1F5F9]">
            Cuentas
          </h1>
          <p className="text-sm text-[#64748B] mt-2 max-w-xl">
            Cada movimiento queda asociado a una cuenta (efectivo, débito, crédito, ahorros, etc.).
            Así puedes ver el saldo real de cada origen del dinero.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#0052FF] hover:bg-[#0040CC] text-white h-11 px-5 font-medium"
          data-testid="accounts-new-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva cuenta
        </Button>
      </section>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="accounts-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
        </div>
      ) : (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="accounts-grid">
          {accounts.map((acc) => {
            const Icon = getAccountIconComp(acc.icon);
            return (
              <div
                key={acc.id}
                className="clay-card p-5 fade-up relative overflow-hidden"
                data-testid={`account-card-${acc.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${acc.color}1A`, color: acc.color }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  {acc.is_default && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#0052FF] bg-[#EEF4FF] px-2 py-1 rounded-full font-medium"
                      data-testid={`account-default-badge-${acc.id}`}
                    >
                      <Star className="w-3 h-3" /> Por defecto
                    </span>
                  )}
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                  {getAccountTypeLabel(acc.type)}
                </p>
                <h3 className="font-display text-xl font-semibold text-[#F1F5F9] mt-1 truncate" data-testid={`account-name-${acc.id}`}>
                  {acc.name}
                </h3>

                <div
                  className="font-numeric text-2xl font-semibold mt-3"
                  style={{ color: (acc.current_balance ?? 0) < 0 ? "#EF4444" : "#F1F5F9" }}
                  data-testid={`account-balance-${acc.id}`}
                >
                  {formatCurrency(acc.current_balance ?? acc.initial_balance, currency)}
                </div>
                {acc.note && (
                  <p className="text-xs text-[#94A3B8] mt-2 truncate">{acc.note}</p>
                )}

                {acc.type === "credit" && (
                  <CreditCardHealth accountId={acc.id} currency={currency} />
                )}

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#2A3447]">
                  {!acc.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => makeDefault(acc)}
                      className="text-xs text-[#0052FF] hover:bg-[#EEF4FF] h-8"
                      data-testid={`account-make-default-${acc.id}`}
                    >
                      <Star className="w-3 h-3 mr-1" /> Predeterminar
                    </Button>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(acc)}
                      className="h-8 w-8 p-0 text-[#64748B] hover:bg-[#1B2638]"
                      data-testid={`account-edit-${acc.id}`}
                      aria-label="Editar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {!acc.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(acc)}
                        className="h-8 w-8 p-0 text-[#EF4444] hover:bg-[#FEE2E2]"
                        data-testid={`account-delete-${acc.id}`}
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131C2E] max-w-md" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#F1F5F9]">
              {editing ? "Editar cuenta" : "Nueva cuenta"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Define el origen del dinero: tipo, color, icono y saldo inicial.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ej. BBVA Débito"
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                maxLength={60}
                required
                data-testid="account-name-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]" data-testid="account-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131C2E]">
                  {ACCOUNT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Icono</Label>
              <div className="mt-2 grid grid-cols-6 gap-2" data-testid="account-icon-grid">
                {ACCOUNT_ICONS.map((opt) => {
                  const I = opt.Icon;
                  const active = form.icon === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, icon: opt.value }))}
                      className={`h-11 rounded-lg flex items-center justify-center border transition-all ${
                        active
                          ? "border-[#0052FF] bg-[#EEF4FF] text-[#0052FF]"
                          : "border-[#2A3447] text-[#64748B] hover:bg-[#1B2638]"
                      }`}
                      data-testid={`account-icon-${opt.value}`}
                      aria-label={opt.label}
                    >
                      <I className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Color</Label>
              <div className="mt-2 grid grid-cols-8 gap-2" data-testid="account-color-grid">
                {ACCOUNT_COLORS.map((c) => {
                  const active = form.color === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, color: c }))}
                      className={`h-9 rounded-lg border-2 transition-all ${
                        active ? "border-[#0F172A] scale-105" : "border-transparent"
                      }`}
                      style={{ backgroundColor: c }}
                      data-testid={`account-color-${c.replace('#', '')}`}
                      aria-label={`color ${c}`}
                    />
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                Saldo inicial
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.initial_balance}
                onChange={(e) => setForm((f) => ({ ...f, initial_balance: e.target.value }))}
                placeholder="0.00"
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric"
                data-testid="account-initial-balance-input"
              />
              {form.type === "credit" && (
                <p className="text-[11px] text-[#64748B] mt-1">
                  Para tarjetas: usa un saldo negativo si ya tienes deuda (ej. <span className="font-numeric">-2500</span>).
                </p>
              )}
            </div>

            {form.type === "credit" && (
              <div
                className="rounded-xl border border-[#2A3447] bg-[#0F172A] p-4 space-y-3"
                data-testid="account-credit-section"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                  Tarjeta de crédito
                </p>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                    Límite de crédito
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.credit_limit}
                    onChange={(e) => setForm((f) => ({ ...f, credit_limit: e.target.value }))}
                    placeholder="Ej. 30000"
                    className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric"
                    data-testid="account-credit-limit-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                      Día de corte
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={form.cutoff_day}
                      onChange={(e) => setForm((f) => ({ ...f, cutoff_day: e.target.value }))}
                      placeholder="15"
                      className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric"
                      data-testid="account-cutoff-day-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                      Día de pago
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={form.payment_due_day}
                      onChange={(e) => setForm((f) => ({ ...f, payment_due_day: e.target.value }))}
                      placeholder="5"
                      className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric"
                      data-testid="account-payment-due-day-input"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-[#64748B] leading-relaxed">
                  Con estos datos calculamos tu <span className="text-[#F1F5F9]">saldo disponible</span>,
                  el monto <span className="text-[#F1F5F9]">para no generar intereses</span> y los días que faltan al próximo corte y pago.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-[#2A3447]"
                data-testid="account-cancel-button"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
                data-testid="account-submit-button"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear cuenta"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
